/**
 * Expiring Contracts Empty State Component
 * US-CONT-005: View Expiring Contracts Alert
 * Displays empty state when no contracts are found
 */

import { FileText, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ExpiringContractsEmptyStateProps {
  hasActiveFilters: boolean;
  onReset?: () => void;
  className?: string;
}

export function ExpiringContractsEmptyState({
  hasActiveFilters,
  onReset,
  className
}: ExpiringContractsEmptyStateProps) {
  return (
    <Card className={cn('border-dashed', className)}>
      <CardContent className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <div className="mb-4 rounded-full bg-muted p-4">
          {hasActiveFilters ? (
            <Search className="h-8 w-8 text-muted-foreground" />
          ) : (
            <FileText className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        <h3 className="mb-2 text-lg font-semibold">
          {hasActiveFilters ? 'No contracts match your filters' : 'No contracts expiring soon'}
        </h3>

        <p className="mb-6 max-w-md text-sm text-muted-foreground">
          {hasActiveFilters
            ? 'Try adjusting your search criteria or filters to find what you\'re looking for.'
            : 'There are no contracts expiring within the selected time period. Check back later or adjust the time range.'}
        </p>

        {hasActiveFilters && onReset && (
          <Button onClick={onReset} variant="outline">
            Clear Filters
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact empty state for dashboard widget
 */
interface CompactEmptyStateProps {
  message?: string;
  className?: string;
}

export function CompactEmptyState({
  message = 'No expiring contracts',
  className
}: CompactEmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-8 px-4 text-center', className)}>
      <div className="mb-3 rounded-full bg-muted p-3">
        <FileText className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
